import {
  Col,
  Row,
  Space,
  notification,
  Dropdown,
  Menu,
  Pagination,
} from "antd";
import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { useNavigate } from "react-router-dom";
import { formatDate } from "../../utils/helpers";
import MobHeader from "../../Components/Header/MobHeader";
import StatusTag from "../../Components/StatusTag";
import {
  ThemeFilterBtn,
  ThemeButton,
  ThemeTableCard,
  ThemeTable,
  ThemeInput,
  ThemeMainWrapper,
  ThemeModal,
  CardPaginationWrapper,
} from "../Styles/Theme";
import {
  ThemeProjectTabWrapper,
} from "../Projects/ProjectsStyle";
import {
  SurveyCard,
  SurveyCardInner,
  SurveyCardHeader,
  SurveyCardTitle,
  SurveyCardDetails,
  SurveyCardDetailRow,
  SurveyCardFooter,
  SurveyCardDate,
  SurveyCardMenuIcon,
  SurveyTableActionWrapper,
  SurveyNameLink,
} from "./SurveysStyle";
import Squares from "../Dashboard/Squares";
import { GreetingText } from "../Dashboard/DashboardStyle";
import { SearchOutlined, RightOutlined } from "@ant-design/icons";
import { Link } from "react-router-dom";
import {
  getSurveysApi,
  getSurveysReset,
  deleteSurveyApi,
  deleteSurveyReset,
} from "../../store/actions/surveyAction";
import { useSurveyPermissions } from "../../utils/permissions";
import useFolders from "../../hooks/useFolders";
import {
  FolderBackNav,
  FolderCardsSection,
  ItemsSectionLabel,
  NewFolderButton,
  FolderModals,
} from "../../Components/FolderComponents";

const mapStatusToDisplay = (status) => {
  const statusMap = {
    'draft': 'Draft',
    'generating': 'In-Progress',
    'ready': 'Ready',
    'published': 'Published',
    'in_progress': 'In-Progress',
    'paused': 'Paused',
    'completed': 'Completed',
  };
  return statusMap[status] || status;
};

const SurveysList = (props) => {
  const {
    getSurveys,
    getSurveysReset,
    getSurveysState,
    deleteSurvey,
    deleteSurveyReset,
    deleteSurveyState,
  } = props;

  const navigate = useNavigate();

  // Permission check - shortcut hook
  const { can } = useSurveyPermissions();

  const [activeView, setActiveView] = useState("card");
  const [tableData, setTableData] = useState([]);
  const [cardData, setCardData] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [selectedSurvey, setSelectedSurvey] = useState(null);

  const defaultFilters = {
    page: 1,
    limit: 12,
    search: '',
    status: '',
    sortBy: 'createdAt',
    sortDirection: 'desc',
  };

  const getFiltersFromUrl = () => {
    const urlParams = new URLSearchParams(window.location.search);
    const urlFilters = {};
    for (const [key, value] of urlParams.entries()) {
      urlFilters[key] = value;
    }
    const filters = {
      ...defaultFilters,
      ...urlFilters,
    };
    return filters;
  };

  const initialFilters = getFiltersFromUrl();
  const [filters, setFilters] = useState(initialFilters);
  const [searchValue, setSearchValue] = useState(initialFilters.search || '');

  // Shared folder hook
  const folderHook = useFolders({ entityType: 'survey', searchValue });
  const {
    folders,
    filteredFolders,
    currentFolderId,
    currentFolderName,
    refreshNeeded,
    createFolderModal,
    setCreateFolderModal,
    renameFolderModal,
    setRenameFolderModal,
    folderToRename,
    deleteFolderModal,
    setDeleteFolderModal,
    folderToDelete,
    moveToFolderModal,
    setMoveToFolderModal,
    selectedFolderId,
    setSelectedFolderId,
    removeFromFolderModal,
    setRemoveFromFolderModal,
    itemToRemove: surveyToRemove,
    foldersCollapsed,
    setFoldersCollapsed,
    itemsCollapsed: surveysCollapsed,
    setItemsCollapsed: setSurveysCollapsed,
    draggedItem: draggedSurvey,
    dragOverFolderId,
    folderForm,
    renameFolderForm,
    createFolderState,
    renameFolderState,
    deleteFolderState,
    addItemToFolderState,
    removeItemFromFolderState,
    fetchFolders,
    getFoldersState,
    handleGetFoldersResponse,
    handleCreateFolderResponse,
    handleRenameFolderResponse,
    handleDeleteFolderResponse,
    handleAddItemToFolderResponse,
    handleRemoveItemFromFolderResponse,
    handleCreateFolder,
    handleRenameFolder,
    handleDeleteFolder,
    handleMoveToFolder,
    handleRemoveFromFolder,
    confirmRemoveFromFolder,
    handleFolderClick,
    handleBackToRoot,
    openRenameFolderModal,
    openDeleteFolderModal,
    openMoveToFolderModal,
    handleDragStart,
    handleDragEnd,
    handleFolderDragOver,
    handleFolderDragEnter,
    handleFolderDragLeave,
    handleFolderDrop,
  } = folderHook;

  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const checkIsMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkIsMobile();
    window.addEventListener("resize", checkIsMobile);
    return () => {
      window.removeEventListener("resize", checkIsMobile);
    };
  }, []);

  // Handle URL-based pagination and filters
  useEffect(() => {
    const updatedFilters = getFiltersFromUrl();
    setFilters({ ...updatedFilters });
    setSearchValue(updatedFilters.search || '');
    if (currentFolderId) {
      getSurveys({ ...updatedFilters, folderId: currentFolderId });
    } else {
      getSurveys({ ...updatedFilters, excludeFoldered: true });
    }
  }, [window.location.search, currentFolderId]);

  const getAgeRange = (demographics) => {
    if (!demographics?.ageRange) return null;
    const { min, max } = demographics.ageRange;
    if (min && max) return `${min}-${max}`;
    if (min) return `${min}+`;
    if (max) return `Up to ${max}`;
    return null;
  };

  const getTargetAudience = (demographics) => {
    if (!demographics) return null;
    return demographics.topic || demographics.country || null;
  };

  // Handle surveys response
  useEffect(() => {
    if (getSurveysState.apiState === 'success' && getSurveysState.data) {
      const surveysData = getSurveysState.data.data || getSurveysState.data;
      const surveys = surveysData.surveys || [];

      if (surveys && surveys.length > 0) {
        setTotalRecords(surveysData.pagination?.total || surveysData.total_count || 0);

        const updatedSurveys = surveys.map((item) => ({
          key: item._id,
          id: item._id,
          name: item.title || '-',
          surveyName: item.title || '-',
          ageRange: getAgeRange(item.demographics),
          targetAudience: getTargetAudience(item.demographics),
          createdOn: formatDate(item.createdAt),
          updatedOn: formatDate(item.updatedAt),
          status: mapStatusToDisplay(item.status || 'draft'),
          surveyStatus: item.status || 'draft',
        }));

        setTableData(updatedSurveys);
        setCardData(updatedSurveys);
      } else {
        setTableData([]);
        setCardData([]);
        setTotalRecords(0);
      }
      getSurveysReset();
    } else if (getSurveysState.apiState === 'error') {
      notification.error({
        message: 'Error',
        description: getSurveysState.message || 'Failed to fetch surveys',
      });
      getSurveysReset();
    }
  }, [getSurveysState, getSurveysReset]);

  // Handle delete response
  useEffect(() => {
    if (deleteSurveyState.apiState === 'success') {
      notification.success({
        message: 'Success',
        description: deleteSurveyState.message || 'Survey deleted successfully',
      });
      setDeleteModalVisible(false);
      setSelectedSurvey(null);
      refreshSurveys();
    } else if (deleteSurveyState.apiState === 'error') {
      notification.error({
        message: 'Error',
        description: deleteSurveyState.message || 'Failed to delete survey',
      });
    }
    deleteSurveyReset();
  }, [deleteSurveyState, deleteSurveyReset, getSurveys]);

  // Helper to re-fetch surveys with current folder context
  const refreshSurveys = () => {
    const updatedFilters = getFiltersFromUrl();
    if (currentFolderId) {
      getSurveys({ ...updatedFilters, folderId: currentFolderId });
    } else {
      getSurveys({ ...updatedFilters, excludeFoldered: true });
    }
  };

  // Folder hook: fetch on mount
  useEffect(() => {
    fetchFolders();
  }, []);

  // Folder hook: response handlers
  useEffect(() => { handleGetFoldersResponse(); }, [getFoldersState]);
  useEffect(() => { handleCreateFolderResponse(); }, [createFolderState]);
  useEffect(() => { handleRenameFolderResponse(); }, [renameFolderState]);
  useEffect(() => { handleDeleteFolderResponse(); }, [deleteFolderState]);
  useEffect(() => { handleAddItemToFolderResponse(); }, [addItemToFolderState]);
  useEffect(() => { handleRemoveItemFromFolderResponse(); }, [removeItemFromFolderState]);

  // Re-fetch surveys when folder hook signals a refresh (after folder operations)
  useEffect(() => {
    if (refreshNeeded > 0) {
      refreshSurveys();
    }
  }, [refreshNeeded]);

  // Build survey detail URL with folder context for back navigation
  const getSurveyDetailUrl = (surveyId) => {
    if (currentFolderId) {
      return `/surveys/study/${surveyId}?fromFolderId=${currentFolderId}&fromFolderName=${encodeURIComponent(currentFolderName)}`;
    }
    return `/surveys/study/${surveyId}`;
  };

  // Build create survey URL with folder context (so new survey gets added to folder)
  const getCreateSurveyUrl = () => {
    if (currentFolderId) {
      return `/surveys/launch-survey?folderId=${currentFolderId}&folderName=${encodeURIComponent(currentFolderName)}`;
    }
    return '/surveys/launch-survey';
  };

  const handleSearch = (value) => {
    const queryParams = new URLSearchParams(window.location.search);
    if (value) {
      queryParams.set('search', value);
    } else {
      queryParams.delete('search');
    }
    queryParams.set('page', '1');
    const newUrl = `${window.location.pathname}?${queryParams.toString()}`;
    window.history.pushState({}, '', newUrl);
    setFilters({ ...filters, search: value, page: 1 });
    setSearchValue(value);
  };

  const handleTableChange = (pagination, _, sorter) => {
    const queryParams = new URLSearchParams(window.location.search);
    queryParams.set('page', pagination.current || 1);
    queryParams.set('limit', pagination.pageSize || 12);

    if (sorter && Object.keys(sorter).length > 0) {
      queryParams.set('sortBy', sorter.field || 'createdAt');
      queryParams.set('sortDirection', sorter.order === 'ascend' ? 'asc' : 'desc');
    } else {
      queryParams.set('sortBy', 'createdAt');
      queryParams.set('sortDirection', 'desc');
    }

    const newUrl = `${window.location.pathname}?${queryParams.toString()}`;
    window.history.pushState({}, '', newUrl);
    setFilters({
      ...filters,
      sortBy: sorter?.field || 'createdAt',
      sortDirection: sorter?.order === 'ascend' ? 'asc' : 'desc',
      page: pagination.current || 1,
      limit: pagination.pageSize || 12,
    });
  };

  const handleView = (record) => {
    navigate(getSurveyDetailUrl(record.id));
  };

  const handleDelete = (record) => {
    setSelectedSurvey(record);
    setDeleteModalVisible(true);
  };

  const confirmDelete = () => {
    if (selectedSurvey) {
      deleteSurvey(selectedSurvey.id);
    }
  };

  const renderCardMenu = (record) => {
    return (
      <Menu
        selectable={false}
        onClick={({ key, domEvent }) => {
          domEvent.stopPropagation();
          if (key === "view") {
            handleView(record);
          } else if (key === "moveToFolder") {
            openMoveToFolderModal(record);
          } else if (key === "removeFromFolder") {
            handleRemoveFromFolder(record);
          } else if (key === "delete") {
            handleDelete(record);
          }
        }}
      >
        <Menu.Item key="view">View Details</Menu.Item>
        <Menu.Item key="moveToFolder">Move to Folder</Menu.Item>
        {currentFolderId && (
          <Menu.Item key="removeFromFolder">Remove from Folder</Menu.Item>
        )}
        {can('delete') && (
          <>
            <Menu.Divider />
            <Menu.Item key="delete">Delete</Menu.Item>
          </>
        )}
      </Menu>
    );
  };

  const columns = [
    {
      title: "Survey Name",
      dataIndex: "surveyName",
      key: "surveyName",
      render: (text, record) => (
        <SurveyNameLink onClick={() => handleView(record)}>
          {text}
        </SurveyNameLink>
      ),
    },
    {
      title: "Age Range",
      dataIndex: "ageRange",
      key: "ageRange",
      render: (text) => <span>{text || '-'}</span>,
    },
    {
      title: "Topic",
      dataIndex: "targetAudience",
      key: "targetAudience",
      render: (text) => <span>{text || '-'}</span>,
    },
    {
      title: "Last Modified",
      dataIndex: "updatedOn",
      key: "updatedOn",
      width: 130,
      sorter: true,
      render: (text) => <span>{text}</span>,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      sorter: true,
      render: (status) => {
        return <StatusTag status={status} />;
      },
    },
    {
      title: "Actions",
      key: "actions",
      align: "center",
      width: 150,
      render: (_, record) => (
        <SurveyTableActionWrapper>
          <ThemeFilterBtn
            shape="circle"
            size="medium"
            icon={<RightOutlined />}
            onClick={() => handleView(record)}
          />
          <Dropdown
            trigger={['click']}
            placement="bottomRight"
            dropdownRender={() => renderCardMenu(record)}
          >
            <ThemeFilterBtn
              shape="circle"
              size="medium"
              icon={<img src="/images/menu-dots-vertical.svg" alt="More" />}
              onClick={(e) => e.stopPropagation()}
            />
          </Dropdown>
        </SurveyTableActionWrapper>
      ),
    },
  ];

  return (
    <>
      <MobHeader />
      <ThemeMainWrapper>
          <Row gutter={[16, 16]} className="mb-6" align="middle">
            <Col
              xs={{ span: 8, order: 1 }}
              sm={{ span: 8, order: 1 }}
              md={{ span: 6, order: 1 }}
              lg={6}
              xl={8}
            >
              <GreetingText>Surveys</GreetingText>
            </Col>
            <Col
              xs={{ span: 24, order: 3 }}
              sm={{ span: 24, order: 3 }}
              md={{ span: 8, order: 1 }}
              lg={8}
              xl={8}
              className="text-center sm:text-center md:text-end lg:text-center xl:text-center"
            >
              <ThemeProjectTabWrapper>
                <div className="view-toggle">
                  <button
                    className={`toggle-btn ${
                      activeView === "card" ? "active" : ""
                    }`}
                    onClick={() => setActiveView("card")}
                  >
                    Card View
                  </button>
                  <button
                    className={`toggle-btn ${
                      activeView === "table" ? "active" : ""
                    }`}
                    onClick={() => setActiveView("table")}
                  >
                    Table View
                  </button>
                </div>
              </ThemeProjectTabWrapper>
            </Col>
            <Col
              xs={{ span: 16, order: 2 }}
              sm={{ span: 16, order: 2 }}
              md={{ span: 10, order: 1 }}
              lg={10}
              xl={8}
              className="text-end"
            >
              <Space gap={10}>
                {can('create') && (
                  isMobile ? (
                    <>
                      {!currentFolderId && (
                        <ThemeFilterBtn
                          className="background"
                          size="large"
                          shape="square"
                          icon={<img src="/images/folder-open-purple.svg" alt="Folder" style={{ width: 18, height: 18 }} />}
                          onClick={() => setCreateFolderModal(true)}
                        />
                      )}
                      <Link to={getCreateSurveyUrl()}>
                        <ThemeFilterBtn
                          className="background"
                          size="large"
                          shape="square"
                          icon={<img src="/images/plus-icon.svg" alt="New Survey" />}
                        />
                      </Link>
                    </>
                  ) : (
                    <>
                      {!currentFolderId && (
                        <NewFolderButton isMobile={false} onClick={() => setCreateFolderModal(true)} />
                      )}
                      <Link to={getCreateSurveyUrl()}>
                        <ThemeButton
                          size="large"
                          icon={<img src="/images/plus-icon.svg" alt="New Survey" />}
                        >
                          New Survey
                        </ThemeButton>
                      </Link>
                    </>
                  )
                )}
              </Space>
            </Col>
          </Row>
          {currentFolderId && (
            <FolderBackNav currentFolderName={currentFolderName} onBack={handleBackToRoot} />
          )}
          <Row gutter={[16, 16]} className="mb-6" align="middle">
            <Col xs={24} sm={12} md={12} lg={10} xl={6} xxl={4}>
              <ThemeInput
                className="shadow"
                placeholder="Search surveys..."
                size="large"
                shape="square"
                prefix={<SearchOutlined />}
                value={searchValue}
                onChange={(e) => {
                  const value = e.target.value;
                  setSearchValue(value);
                  if (!value) {
                    handleSearch('');
                  }
                }}
                onPressEnter={(e) => handleSearch(e.target.value)}
              />
            </Col>
          </Row>
          {activeView === "card" && (
            <>
              {/* Folder section (only at root, not inside a folder) */}
              {!currentFolderId && (
                <>
                  <FolderCardsSection
                    filteredFolders={filteredFolders}
                    foldersCollapsed={foldersCollapsed}
                    setFoldersCollapsed={setFoldersCollapsed}
                    onFolderClick={handleFolderClick}
                    onRenameFolderClick={openRenameFolderModal}
                    onDeleteFolderClick={openDeleteFolderModal}
                    enableDrag={true}
                    dragOverFolderId={dragOverFolderId}
                    onDragOver={handleFolderDragOver}
                    onDragEnter={handleFolderDragEnter}
                    onDragLeave={handleFolderDragLeave}
                    onDrop={handleFolderDrop}
                  />
                  {filteredFolders.length > 0 && cardData.length > 0 && (
                    <ItemsSectionLabel label="Surveys" itemsCollapsed={surveysCollapsed} setItemsCollapsed={setSurveysCollapsed} />
                  )}
                </>
              )}
              {!(surveysCollapsed && filteredFolders.length > 0 && !currentFolderId) && (
              <>
              <Row gutter={[16, 16]} className="mb-6">
                {getSurveysState.apiState === 'loading' ? (
                  Array.from({ length: 6 }).map((_, index) => (
                    <Col span={24} sm={12} md={12} lg={12} xl={8} xxl={6} xxxl={4} key={`skeleton-${index}`}>
                      <SurveyCard loading={true} />
                    </Col>
                  ))
                ) : cardData.length > 0 ? (
                  cardData.map((item) => (
                    <Col
                      span={24} sm={12} md={12} lg={12} xl={8} xxl={6} xxxl={4} key={item.key}
                      draggable={!currentFolderId && filteredFolders.length > 0}
                      onDragStart={!currentFolderId && filteredFolders.length > 0 ? (e) => handleDragStart(e, item) : undefined}
                      onDragEnd={!currentFolderId && filteredFolders.length > 0 ? handleDragEnd : undefined}
                      style={!currentFolderId && filteredFolders.length > 0 ? { cursor: 'grab' } : undefined}
                    >
                      <SurveyCard onClick={() => handleView(item)}>
                        <SurveyCardInner>
                          <SurveyCardHeader>
                            <SurveyCardTitle>{item.surveyName}</SurveyCardTitle>
                            <Dropdown
                              trigger={['click']}
                              placement="bottomRight"
                              dropdownRender={() => renderCardMenu(item)}
                            >
                              <SurveyCardMenuIcon onClick={(e) => e.stopPropagation()}>
                                <img src="/images/menu-dots-vertical.svg" alt="Menu" />
                              </SurveyCardMenuIcon>
                            </Dropdown>
                          </SurveyCardHeader>
                          <SurveyCardDetails>
                            <SurveyCardDetailRow>
                              <span className="label">Age -</span>
                              <span className="value">{item.ageRange || '-'}</span>
                            </SurveyCardDetailRow>
                            <SurveyCardDetailRow>
                              <span className="label">Topic -</span>
                              <span className="value">{item.targetAudience || '-'}</span>
                            </SurveyCardDetailRow>
                          </SurveyCardDetails>
                          <SurveyCardFooter className="mt-1">
                            <StatusTag status={item.status} />
                          </SurveyCardFooter>
                          <Space className="justify-between">
                            <SurveyCardDate>
                              <img src="/images/calendar-icon.svg" alt="Calendar" />
                                <span>Last Modified - {item.updatedOn}</span>
                              </SurveyCardDate>
                              <ThemeFilterBtn
                                className="btnBorder"
                                shape="circle"
                                size="medium"
                                icon={<img src="/images/arrow-right.svg" alt="#" />}
                              />
                            </Space>

                        </SurveyCardInner>
                      </SurveyCard>
                    </Col>
                  ))
                ) : (
                  <Col span={24} className="text-center">
                    <p>{currentFolderId ? 'No surveys in this folder' : 'No surveys found'}</p>
                  </Col>
                )}
              </Row>
              {totalRecords > 0 && (
                <CardPaginationWrapper>
                  <Pagination
                    current={parseInt(filters.page) || 1}
                    pageSize={parseInt(filters.limit) || 12}
                    total={totalRecords}
                    showSizeChanger={true}
                    showTotal={(total, range) =>
                      `${range[0]}-${range[1]} of ${total} surveys`
                    }
                    onChange={(page, pageSize) => handleTableChange({ current: page, pageSize }, null, {})}
                  />
                </CardPaginationWrapper>
              )}
              </>
              )}
            </>
          )}
          {activeView === "table" && (
            <>
              {/* Folder navigation for table view */}
              {!currentFolderId && (
                <>
                  <FolderCardsSection
                    filteredFolders={filteredFolders}
                    foldersCollapsed={foldersCollapsed}
                    setFoldersCollapsed={setFoldersCollapsed}
                    onFolderClick={handleFolderClick}
                    onRenameFolderClick={openRenameFolderModal}
                    onDeleteFolderClick={openDeleteFolderModal}
                  />
                  {filteredFolders.length > 0 && (
                    <ItemsSectionLabel label="Surveys" itemsCollapsed={surveysCollapsed} setItemsCollapsed={setSurveysCollapsed} />
                  )}
                </>
              )}
              {!(surveysCollapsed && filteredFolders.length > 0 && !currentFolderId) && (
              <ThemeTableCard>
                <Row gutter={[16, 16]}>
                  <Col xs={24}>
                    <ThemeTable
                      columns={columns}
                      dataSource={tableData}
                      pagination={{
                        current: parseInt(filters.page) || 1,
                        pageSize: parseInt(filters.limit) || 12,
                        total: totalRecords,
                        showSizeChanger: true,
                        showTotal: (total, range) =>
                          `${range[0]}-${range[1]} of ${total} surveys`,
                      }}
                      onChange={handleTableChange}
                      loading={getSurveysState.apiState === 'loading'}
                      scroll={{ x: 768 }}
                    />
                  </Col>
                </Row>
              </ThemeTableCard>
              )}
            </>
          )}
      </ThemeMainWrapper>

      {/* Delete Modal */}
      <ThemeModal
        title="Delete Survey"
        open={deleteModalVisible}
        onOk={confirmDelete}
        onCancel={() => {
          setDeleteModalVisible(false);
          setSelectedSurvey(null);
        }}
        confirmLoading={deleteSurveyState.apiState === 'loading'}
        okText="Delete"
        cancelText="Cancel"
        centered
      >
        <p>
          {`Are you sure you want to delete survey "${selectedSurvey?.surveyName}"?`}
        </p>
        <p>This action cannot be undone.</p>
      </ThemeModal>

      {/* Folder Modals (Create, Rename, Delete, Move, Remove) */}
      <FolderModals
        createFolderModal={createFolderModal}
        setCreateFolderModal={setCreateFolderModal}
        folderForm={folderForm}
        handleCreateFolder={handleCreateFolder}
        createFolderState={createFolderState}
        renameFolderModal={renameFolderModal}
        setRenameFolderModal={setRenameFolderModal}
        folderToRename={folderToRename}
        renameFolderForm={renameFolderForm}
        handleRenameFolder={handleRenameFolder}
        renameFolderState={renameFolderState}
        deleteFolderModal={deleteFolderModal}
        setDeleteFolderModal={setDeleteFolderModal}
        folderToDelete={folderToDelete}
        handleDeleteFolder={handleDeleteFolder}
        deleteFolderState={deleteFolderState}
        removeFromFolderModal={removeFromFolderModal}
        setRemoveFromFolderModal={setRemoveFromFolderModal}
        itemToRemove={surveyToRemove}
        confirmRemoveFromFolder={confirmRemoveFromFolder}
        removeItemFromFolderState={removeItemFromFolderState}
        moveToFolderModal={moveToFolderModal}
        setMoveToFolderModal={setMoveToFolderModal}
        itemToMove={folderHook.itemToMove}
        selectedFolderId={selectedFolderId}
        setSelectedFolderId={setSelectedFolderId}
        handleMoveToFolder={handleMoveToFolder}
        addItemToFolderState={addItemToFolderState}
        folders={folders}
        currentFolderId={currentFolderId}
        itemLabel="survey"
      />
    </>
  );
};

const mapStateToProps = (state) => ({
  getSurveysState: state.survey.getSurveysState,
  deleteSurveyState: state.survey.deleteSurveyState,
});

const mapDispatchToProps = (dispatch) => ({
  getSurveys: (params) => dispatch(getSurveysApi(params)),
  getSurveysReset: () => dispatch(getSurveysReset()),
  deleteSurvey: (id) => dispatch(deleteSurveyApi(id)),
  deleteSurveyReset: () => dispatch(deleteSurveyReset()),
});

export default connect(mapStateToProps, mapDispatchToProps)(SurveysList);
